import {Component, OnInit, inject, output, input} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {ViewMode, Right, ProjectStatus, TeamRole} from "../../../interfaces";
import {DataService} from "../../../../services/data.service";
import {FormService} from "../../../../services/form.service";
import {AccessService} from "../../../../services/access.service";
import moment from "moment/moment";
import {ToastrService} from "ngx-toastr";
import {ProjectService} from "../../../../services/project.service";
import Swal from "sweetalert2";
import {ProjectStatusPipe} from "../../../../pipes/project-status.pipe";
import {NgIf, NgFor, DecimalPipe, DatePipe} from '@angular/common';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {DisableControlDirective} from '../../../../directives/disable-control.directive';
import {YesNoPipe} from '../../../../pipes/yes-no.pipe';

@Component({
    selector: 'app-research-dates',
    templateUrl: './research-dates.component.html',
    styleUrl: './research-dates.component.scss',
    imports: [NgIf, TooltipModule, FormsModule, ReactiveFormsModule, DisableControlDirective, NgFor, DecimalPipe, DatePipe, YesNoPipe]
})
export class ResearchDatesComponent implements OnInit {

    formService = inject(FormService);
    accessService = inject(AccessService);
    projectService = inject(ProjectService);
    private dataService = inject(DataService);
    private fb = inject(UntypedFormBuilder);
    private toastrService = inject(ToastrService);
    private projectStatusPipe = inject(ProjectStatusPipe);

    readonly PID = input.required<any>();
    readonly reloadTrigger = input<number>(0);

    readonly changeProject = output();
    readonly changeViewMode = output<boolean>();

    readonly ViewMode = ViewMode;
    readonly Right = Right;
    readonly ProjectStatus = ProjectStatus;
    readonly TeamRole = TeamRole;

    viewMode!: ViewMode;
    project: any;
    form!: UntypedFormGroup;

    ngOnInit(): void {
        this.viewMode = ViewMode.VIEW;

        this.form = this.fb.group({
            id: [null],
            dates_start_planned: [null],
            dates_start_actual: [null],
            dates_start_comment: [null],
            dates_end_planned: [null],
            dates_end_actual: [null],
            dates_end_comment: [null],
            milestones: this.fb.array([]),
        });

        if (this.PID() !== '0') {
            this.getProject();
        }

    }

    getProject(): void {
        this.dataService.request('Research/get', {
            PID: this.PID()
        }).subscribe(response => {
            this.project = response.project;

            if (this.project.dates_start_planned && this.project.dates_start_actual) {
                this.project.dates_start_deviation = moment(this.project.dates_start_actual).diff(moment(this.project.dates_start_planned), 'days');
            }
            if (this.project.dates_end_planned && this.project.dates_end_actual) {
                this.project.dates_end_deviation = moment(this.project.dates_end_actual).diff(moment(this.project.dates_end_planned), 'days');
            }

            if (this.project.dates_start_planned && this.project.dates_end_planned) {
                this.project.duration = moment(this.project.dates_end_planned).diff(moment(this.project.dates_start_planned), 'months', true);
            } else {
                this.project.duration = null;
            }

            this.patchForm();
        });
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.project.id,
            dates_start_planned: this.project.dates_start_planned,
            dates_start_actual: this.project.dates_start_actual,
            dates_start_comment: this.project.dates_start_comment,
            dates_end_planned: this.project.dates_end_planned,
            dates_end_actual: this.project.dates_end_actual,
            dates_end_comment: this.project.dates_end_comment,
        });

        this.formService.getFormArray(this.form, 'milestones').clear();
        this.project.milestones.forEach(item => {
            this.addFormItem(item, 'milestones');
        });
    }

    addFormItem(data: any = {}, control): void {
        this.formService.getFormArray(this.form, control).push(
            this.fb.group({
                id: [data.id || null],
                id_project: [data.id_project || this.project.id],
                text: [data.text || '', Validators.required],
                planned: [data.planned || null],
                actual: [data.actual || null],
                completed: [data.completed ? data.completed == '1' : null],
                _deleted: [false],
            })
        );
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
        if (this.viewMode == ViewMode.EDIT) {
            this.changeViewMode.emit(true);
        } else {
            this.changeViewMode.emit(false);
        }
    }

    update(): void {
        if (![ProjectStatus.REVISE].includes(this.project.status)) {
            this.dataService.request('Research/updateDates', {
                project: this.form.getRawValue(),
            }).subscribe(() => {
                this.toastrService.success('Erfolgreich gespeichert')
                this.setMode(ViewMode.VIEW);
                this.getProject();
                this.changeProject.emit();
            });
        } else {
            Swal.fire({
                title: 'Sicher?',
                text: 'Der Status wird auf "' + this.projectStatusPipe.transform(ProjectStatus.IN_ELABORATION) + '" gesetzt.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#DD6B55',
                confirmButtonText: 'Ja',
                cancelButtonText: 'Nein'
            }).then(result => {
                if (result.value) {
                    this.dataService.request('Research/setStatus', {
                        PID: this.PID(),
                        status: ProjectStatus.IN_ELABORATION,
                    }).subscribe(() => {
                        this.dataService.request('Research/updateDates', {
                            project: this.form.getRawValue(),
                        }).subscribe(() => {
                            this.toastrService.success('Erfolgreich gespeichert')
                            this.setMode(ViewMode.VIEW);
                            this.getProject();
                            this.changeProject.emit();
                        });
                    });
                }
            });
        }
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
    }

    markEntryAsDeleted(entry): void {
        entry.patchValue({
            _deleted: true,
        });
        for (const key in entry.controls) {
            entry.get(key).clearValidators();
            entry.get(key).updateValueAndValidity();
        }
    }

    getLengthOfFormArray(name): number {
        return this.formService.getFormArray(this.form, name).controls.filter(control => {
            return control.value._deleted == false;
        }).length;
    }
}
